import { FC } from 'react';

import CheckCircleIcon from '@/assets/check-circle.svg?react';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import cn from '@/utils/cn';

import { DOCUMENT_PRICES, DOCUMENT_TYPE, type DocumentType } from '../schema';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const DOCUMENT_TYPE_NAME = 'document_type';

interface Props {
  name?: string;
}

interface DocumentCardProps {
  value: DocumentType;
  price: number;
  title: string;
  description: string;
  features: string[];
  recommended?: boolean;
  selected: boolean;
  onSelect: () => void;
}

const DocumentCard = ({
  price,
  title,
  description,
  features,
  recommended,
  selected,
  onSelect,
}: DocumentCardProps) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      'relative flex w-full flex-col gap-3 rounded-lg border p-5 text-left transition-colors duration-150 md:p-5',
      selected
        ? 'border-green-500 bg-green-50'
        : 'border-gray-500 bg-white hover:border-green-300'
    )}
  >
    {recommended && (
      <span className="absolute right-2 top-2 rounded-[4px] bg-green-150 px-2 py-1.5 text-[11px] font-bold uppercase text-green-500">
        Polecane
      </span>
    )}

    <div className="flex items-center gap-2.5">
      <div
        className={cn(
          'size-6 shrink-0 rounded-full bg-white transition-colors duration-150',
          selected ? 'border-7 border-green-500' : 'border border-dark-gray-100'
        )}
      />
      <span className="h2 text-oxford-blue-500">{price} zł</span>
    </div>

    <div>
      <h4 className="h4 text-oxford-blue-500 md:leading-[26px]">{title}</h4>
      <p className="caption mt-1.5 text-dark-gray-400">{description}</p>
    </div>

    <ul className="flex flex-col gap-1.5">
      {features.map((feature) => (
        <li key={feature} className="flex items-start gap-2">
          <CheckCircleIcon
            width={16}
            className="mt-0.5 shrink-0 text-green-500"
          />
          <span className="caption-large text-dark-gray-500">{feature}</span>
        </li>
      ))}
    </ul>
  </button>
);

const DocumentTypeSelect: FC<Props> = ({ name = DOCUMENT_TYPE_NAME }) => {
  const { i18n } = useLingui();
  const { control } = useFormContext();

  const templateFeatures = [
    i18n.t('Szablon dla współwłaścicieli'),
    i18n.t('Miejsce na dane Twojej działki'),
    i18n.t('Możliwość pobrania po zakupie'),
  ];

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        validate: (value) => !!value || i18n.t('Wybierz rodzaj dokumentu'),
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormItem>
          <FormControl>
            <div className="grid grid-cols-1 gap-3 rounded-md bg-gray-200 p-[14px] sm:grid-cols-2 md:p-5">
              <DocumentCard
                value={DOCUMENT_TYPE.TEMPLATE}
                price={DOCUMENT_PRICES[DOCUMENT_TYPE.TEMPLATE]}
                title={i18n.t('Szablon do samodzielnego wypełnienia')}
                description={i18n.t(
                  'Dla osób, które chcą samodzielnie uzupełnić dokument.'
                )}
                features={templateFeatures}
                selected={value === DOCUMENT_TYPE.TEMPLATE}
                onSelect={() => onChange(DOCUMENT_TYPE.TEMPLATE)}
              />
            </div>
          </FormControl>
          {error && (
            <p className="caption-large text-red-400">{error.message}</p>
          )}
        </FormItem>
      )}
    />
  );
};

export default DocumentTypeSelect;
